import { useState, useCallback } from "react";
import { ApiDataResponse, ApiResponse, User } from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

export const useUserService = () => {
  const { setUsers } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const createUser = useCallback(
    async (
      user: User,
      onSuccess?: (user: User) => void
    ): Promise<{ result: ApiDataResponse<User> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<User>(
          "/users",
          axiosClient
        ).create(user)) as ApiDataResponse<User>;

        if (result.succeeded && result.data) {
          const createdUser = result.data;
          setUsers((prev: User[]) => [...prev, createdUser]);
          handleApiSuccess("User created successfully");
          onSuccess?.(createdUser);
          return { result };
        } else {
          handleApiError(result, "Failed to create user");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to create user");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create user",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setUsers]
  );

  const updateUser = useCallback(
    async (
      id: string,
      user: User,
      onSuccess?: (user: User) => void
    ): Promise<{ result: ApiDataResponse<User> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = (await new ApiService<User>(
          "/users",
          axiosClient
        ).update(id, user)) as ApiDataResponse<User>;

        if (result.succeeded) {
          const updatedUser = user;
          setUsers((prev: User[]) =>
            prev.map((u) => (u.id === id ? updatedUser : u))
          );
          handleApiSuccess("User updated successfully");
          onSuccess?.(updatedUser);
          return { result };
        } else {
          handleApiError(result, "Failed to update user");
          return { result: { ...result, data: null as any } };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update user");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update user",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setUsers]
  );

 

  const deleteUser = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<User>("/users", axiosClient).delete(
          id
        );
        if (result.succeeded) {
          setUsers((prev: User[]) => prev.filter((u) => u.id !== id));
          handleApiSuccess("User deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete user");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to delete user");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete user",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setUsers]
  );

  return {
    createUser,
    updateUser,
    deleteUser,
    isLoading,
    errors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
  };
};
